import { useTranslation } from 'next-i18next';
import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  ApiUserPreferencesInformationsOk,
  ApiUserSubscriptionInfoOk,
} from '@type/v1-api-types/user-subscription';
import {
  bjToast, Button, Card, Modal,
} from '@components/common';
import { StatisticValue } from '@components/templates/employer/subscription/cards/StatisticValue';
import { useSwrToggleAutoApply } from '@hooks/useSwrApi/endpoints/user-subscription';
import { useExtendedSearchParams } from '@hooks/next-routing-wrappers';
import { isApiV1Errors } from '@hooks/useSwrApi';
import { ExclamationTriangleIcon, NoSymbolIcon, AdjustmentsVerticalIcon } from '@heroicons/react/24/solid';
import {
  ApplicationSummaryEditAutomatedJobsModal,
} from '@components/templates/talent/premium-page/modal/ApplicationSummaryEditAutomatedJobsModal';
import { useSwrPreferencesAutoApply } from '@hooks/useSwrApi/endpoints/user-subscription/useSwrPreferencesAutoApply';

// Interface
// *****************************************************
interface InternalApplicationSummaryProps {
  locale: Locale,
  userSubscriptionList?: ApiUserSubscriptionInfoOk
}


/**
 * @description Internal Application Summary component.
 */
export const TalentPremiumInternalApplicationSummary = (props: InternalApplicationSummaryProps) => {
  // Destructure props
  const {
    locale,
    userSubscriptionList,
  } = props;

  // Translation
  const { t } = useTranslation('common');

  // Router
  const { searchParams } = useExtendedSearchParams();

  // User Subscription Info
  // We don't want to recalculate on every render, so we use useMemo. It will recalculate only when userSubscriptionList changes
  const userSubscriptionInfo = useMemo(
    () => (Array.isArray(userSubscriptionList) && userSubscriptionList[0] ? userSubscriptionList[0] : null),
    [userSubscriptionList],
  );

  // Auto-apply Enabled State
  const [
    autoApplyEnabled, setAutoApplyEnabled,
  ] = useState(!!userSubscriptionInfo?.autoApplyEnabled);

  // SWR hook for toggle auto apply
  const {
    trigger,
    isMutating,
  } = useSwrToggleAutoApply(locale);


  const {
    trigger: triggerPreferencesAutoApply,
    data,
    isMutating: isMutatingInfo,
  } = useSwrPreferencesAutoApply(locale);

  const [isEditUserPreferencesModalOpen, setIsEditUserPreferencesModalOpen] = useState(false);


  const getAutoApplyPreferences = useCallback(() => {
    void triggerPreferencesAutoApply()
      .then((response) => {
        if (!response) {
          bjToast.error(t('global.general.error'));
          return;
        }
        if (isApiV1Errors(response)) {
          bjToast.error(response.errors[0].message);
          return;
        }
        setIsEditUserPreferencesModalOpen(true);
      });
  }, [triggerPreferencesAutoApply, t]);


  // Toggle auto apply function
  const toggleAutoApply = () => {
    void trigger()
      .then((response) => {
        if (response) {
          if (!isApiV1Errors(response)) {
            bjToast.success(autoApplyEnabled ? t('premium.talent.applicationSummary.toast.autoApply.disabled') : t('premium.talent.applicationSummary.toast.autoApply.enabled'));
            setAutoApplyEnabled((prevState) => !prevState);
            if (!autoApplyEnabled) {
              getAutoApplyPreferences();
            }
          } else {
            bjToast.error(response.errors[0].message);
          }
        } else {
          bjToast.error(t('global.general.error'));
        }
      });
  };


  const premiumModalValue = searchParams?.get('premium-modal');

  useEffect(() => {
    if (premiumModalValue === '1') {
      getAutoApplyPreferences();
    }
  }, [premiumModalValue, getAutoApplyPreferences]);


  // If there are is no user subscription info, don't render anything
  // *****************************************
  if (!userSubscriptionInfo) return null;

  // Render component
  // *****************************************
  return (
    <div className="container mb-5 lg:mb-10">
      {/* Stats Summary */}
      <Card className="rounded bg-surface">
        {/* Stats category title */}
        <Card.Header className="px-3 py-4">
          <strong>{t('premium.talent.applicationSummary.internalApplications')}</strong>
        </Card.Header>

        {/* Application summary numbers. Order is: total, consumed, remaining */}
        <Card.Body className="bg-surface px-3 py-0">
          {userSubscriptionInfo.applicationSummary && (
            <div className="grid grid-cols-3 divide-x rounded bg-surface-50 py-2 text-center">
              <StatisticValue
                name={t('global.label.total.only')}
                value={userSubscriptionInfo.applicationSummary?.total}
                mainColor="text-ink"
              />
              <StatisticValue
                name={t('global.label.used-only')}
                value={userSubscriptionInfo.applicationSummary?.consumed}
                mainColor="text-ink"
              />
              <StatisticValue
                name={t('global.label.available.singular')}
                value={userSubscriptionInfo.applicationSummary?.remaining}
                mainColor="text-secondary"
              />
            </div>
          )}

          {/* Auto-apply elements */}
          <div className="py-4">
            {/* Auto-apply enabled */
              autoApplyEnabled && (
                <div className="sm:flex sm:items-center sm:justify-between">
                  <Button
                    color="ink"
                    styling="text"
                    size="sm"
                    onClick={toggleAutoApply}
                    isLoading={isMutating}
                    className="!font-medium"
                  >
                    {!isMutating && <NoSymbolIcon className="mr-1.5 size-5 text-error" />}
                    {t('premium.talent.applicationSummary.disableAutoApply')}
                  </Button>

                  <Button
                    color="ink"
                    styling="text"
                    size="sm"
                    onClick={getAutoApplyPreferences}
                    isLoading={isMutatingInfo}
                    className="!font-medium"
                  >
                    {!isMutatingInfo && <AdjustmentsVerticalIcon className="mr-1.5 size-5 text-primary-light" />}
                    {t('premium.talent.applicationSummary.editSettings')}
                  </Button>
                </div>
              )
            }

            {/* Auto-apply disabled */
              !autoApplyEnabled && (
                <div className="flex items-center text-sm">
                  <ExclamationTriangleIcon className="mr-1 size-5 text-error" />
                  <span>{t('premium.talent.applicationSummary.internalApplicationsDisabled')}</span>
                  <Button
                    color="primary-light"
                    styling="text"
                    size="sm"
                    onClick={toggleAutoApply}
                    isLoading={isMutating}
                    className="ml-1.5 !font-medium underline"
                  >
                    {t('premium.talent.applicationSummary.enableAutoApply')}
                  </Button>
                </div>
              )
            }
          </div>
        </Card.Body>
      </Card>

      {/* Edit auto-apply preferences modal */}
      <Modal
        size="md"
        isOpen={isEditUserPreferencesModalOpen && data !== undefined}
        header={t('premium.talent.applicationSummary.modal.title')}
        body={(
          <ApplicationSummaryEditAutomatedJobsModal
            informations={data as ApiUserPreferencesInformationsOk}
            locale={locale}
            onClose={() => {
              setIsEditUserPreferencesModalOpen(false);
            }}
            onDeactivate={() => {
              toggleAutoApply();
              setIsEditUserPreferencesModalOpen(false);
            }}
          />
        )}
        onClose={() => {
          setIsEditUserPreferencesModalOpen(false);
        }}
      />
    </div>
  );
};

